import { mapActions, mapMutations } from "vuex";
import { defineComponent, computed } from "vue";
import type { ProductPrice } from "@/types/shop";

interface BasketId {
  value: number;
  productId: number;
  slug: string;
  productGuid: string;
}

interface Brand {
  id: number;
  name: string;
  title: string;
  percent: number | null;
  type: string;
}

interface BrandSeries {
  id: number;
  name: string;
  title: string;
}

interface PromotionsProduct {
  id: number;
  image: string;
  brand: Brand;
  showUnauthorizedText: boolean;
  unauthorizedText: string;
  buttonType: string;
  link: string | null;
  basketIds: BasketId[];
}

interface ResponseProduct {
  id: number;
  productGroupId: number;
  slug: string;
  position: number;
  type: string;
  isPopular: boolean;
  isSample: boolean;
  isNew: boolean;
  isComingSoon: boolean;
  image: string;
  image2: string;
  video: null | string;
  averageRating: number;
  title: string;
  subTitle: string;
  shortDescription: string;
  description: string;
  syllable: string;
  videoTitle: string;
  metaTitle: string;
  metaKeywords: string;
  metaDescription: string;
  giftDescription: string;
  hasActivePromotion: boolean;
  brand: Brand;
  brandSeries: BrandSeries;
  productPrices: {
    data: Array<ProductPrice>;
  };
  images: Array<any>;
  recommendations: Array<any>;
  faqs: Array<any>;
  feedbacks: Array<any>;
  hasDuplicate: boolean;
  unauthorizedProductPriceId: number;
  giftProduct: any;
}

export default defineComponent({
  name: "promotions-product",
  props: {
    promotionsProduct: {
      type: Object as () => PromotionsProduct,
      required: true,
    },
  },
  data() {
    return {
      timeRemaining: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      timerInterval: null,
      isExpired: false,
    };
  },
  computed: {
    formattedTimeRemaining() {
      if (this.isExpired) {
        return null;
      }
      const { days, hours, minutes, seconds } = this.timeRemaining;

      // Hide timer if everything is 0
      if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
        return null;
      }

      const pluralizeDays = (days: number): string => {
        const mod10 = days % 10;
        const mod100 = days % 100;
        if (mod10 === 1 && mod100 !== 11) return `${days} день`;
        // @ts-ignore
        if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return `${days} дні`;
        return `${days} днів`;
      };

      if (days === 0) {
        // @ts-ignore
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      }

      // @ts-ignore
      return `${pluralizeDays(days)} ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    },

    imageWrapperComponent(): string {
      return this.promotionsProduct.buttonType === "link" ? "a" : "RouterLink";
    },

    imageWrapperProps(): Record<string, any> {
      if (this.promotionsProduct.buttonType === "link") {
        return {
          href: this.promotionsProduct.link,
        };
      }

      if (this.promotionsProduct.basketIds.length === 1) {
        return {
          // @ts-ignore
          to: this.$localize({
            name: "card",
            params: {
              slug: this.promotionsProduct.basketIds[0].slug,
            },
          }),
        };
      } else {
        return {
          // @ts-ignore
          to: this.$localize({
            name: "products",
            query: {
              sale_banners: this.promotionsProduct.id,
            },
          }),
        };
      }
    },

    buyButtonComponent(): string {
      if (this.promotionsProduct.showUnauthorizedText) {
        return "button";
      }

      if (this.promotionsProduct.buttonType === "link") {
        return "a";
      }

      return "button";
    },

    buyButtonProps(): Record<string, any> {
      if (this.promotionsProduct.showUnauthorizedText) {
        return {
          type: "button",
        };
      }

      if (this.promotionsProduct.buttonType === "link") {
        return {
          href: this.promotionsProduct.link,
        };
      }

      return {
        type: "button",
      };
    },
  },
  created() {
    this.$nextTick(() => {
      this.startTimer();
    });
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    ...mapMutations({
      addToBasket: `basket/ADD_ITEM_TO_BASKET`,
      changeBasketPopup: "basket/SHOW_BASKET_POPUP",
      changeSeminarDetails: "popups/SHOW_SEMINAR_DETAILS_POPUP",
    }),
    ...mapActions({
      fetchPromotionsProductsForBasket: "promotions/FETCH_PROMOTIONS_PRODUCTS_FOR_BASKET",
    }),
    parsePromotionEndTime(activeTo: string) {
      const [time, date] = activeTo.split(" ");
      const [hours, minutes, seconds] = time.split(":").map(Number);
      const [day, month, year] = date.split("-").map(Number);

      return new Date(year, month - 1, day, hours, minutes, seconds);
    },
    calculateTimeRemaining() {
      // @ts-ignore
      if (!this.promotionsProduct?.activeTo) {
        return;
      }

      // @ts-ignore
      const endTime = this.parsePromotionEndTime(this.promotionsProduct.activeTo);
      const now = new Date();
      const timeDiff = endTime.getTime() - now.getTime();

      if (timeDiff <= 0) {
        this.isExpired = true;
        this.timeRemaining = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
        return;
      }

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      this.timeRemaining = { days, hours, minutes, seconds };
      this.isExpired = false;
    },
    startTimer() {
      this.calculateTimeRemaining();
      // @ts-ignore
      this.timerInterval = setInterval(() => {
        this.calculateTimeRemaining();
      }, 1000);
    },
    handleBuyClick(): void {
      if (this.promotionsProduct.showUnauthorizedText) {
        this.showUnavailableModal();
        return;
      }

      if (this.promotionsProduct.buttonType === "basket") {
        this.addAllProductsToBasket();
        return;
      }
    },
    async navigateToProduct() {
      const ids = (this.promotionsProduct as PromotionsProduct).basketIds.map(({ productId }) => productId);
      // if (ids.length > 1) {
      //   // @ts-ignore
      //   await this.$router.push(this.$localize({
      //     name: "products",
      //     query: { brand: (this.promotionsProduct as PromotionsProduct).brand.name, sort_filter_type: "with_promotions" },
      //   }));
      // } else {
      const response = await this.fetchPromotionsProductsForBasket(ids);
      const slug = response.data[0].slug;
      // @ts-ignore
      await this.$router.push(
        // @ts-ignore
        this.$localize({
          name: "card",
          params: {
            slug: slug,
          },
        })
      );
      // }
    },
    showUnavailableModal() {
      this.changeSeminarDetails({
        status: true,
        data: {
          title: "",
          detail: this.promotionsProduct.unauthorizedText,
        },
      });
    },

    async addAllProductsToBasket(): Promise<void> {
      const ids = this.promotionsProduct.basketIds.map(({ productId }) => productId);
      const guids = this.promotionsProduct.basketIds.map(({ productGuid }) => productGuid).filter(Boolean);
      const response = await this.fetchPromotionsProductsForBasket({ ids, guids });
      if (!response.data.length) return;

      response.data.map((product: ResponseProduct) => {
        const currentProduct = this.promotionsProduct.basketIds.find(item => item.productId === product.id);
        if (!currentProduct) return;
        const currentProductPrice = product.productPrices.data.find(
          item => item.productGuid === currentProduct.productGuid
        );
        if (!currentProductPrice) return;
        this.addToBasket({
          select_count: 1,
          product_price: currentProductPrice,
          product: product,
        });
      });

      this.changeBasketPopup(true);
    },
  },
});
